import React, { useEffect, useState, memo, useRef } from 'react';
import { Box, Divider, Typography, Chip } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useApi } from '../../../../../../shared/hooks/useApi';
import CustomTable from '../../../../../../shared/components/table/custom-table';
import { LoaderComponent } from '../../../../../../shared/components/loaders/page-loader';
import { fetchProductaddDetails } from './product-api';
import { useTransactionCertificateStore } from '../../../../../../store/transaction-certificates.store';
import { flattenCertifiedRawMaterialGeographicInformation, normalizeProductFacilityInformation } from '../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto';
import { useFormActions } from '../../../../../../shared/hooks/form-builder.hooks';
import { formatTcV4Decimal } from '../../../tc-v4-decimals';

interface RenderDetailPanelProps {
  row: any;
}

// Column Definitions
const firstProcessorColumns = [
  { accessorKey: 'tcProductFirstProcessorTeId', header: 'First Processor TE-ID', minSize: 60, maxSize: 150 },
  {
    accessorKey: 'tcProductFirstProcessCode',
    header: 'First Process Code',
    minSize: 60,
    maxSize: 150,
    Cell: ({ row }: any) => {
      const value = row.original.tcProductFirstProcessCode;
      return Array.isArray(value) ? value.join(', ') : value || '';
    },
  },
];

const facilitiesColumns = [
  { accessorKey: 'tcProductFacilitiesTeId', header: 'Facilities TE-ID', minSize: 60, maxSize: 150 },
  {
    accessorKey: 'tcProductFacilitiesProcessCode',
    header: 'Process Code',
    minSize: 60,
    maxSize: 150,
    Cell: ({ row }: any) => {
      const value = row.original.tcProductFacilitiesProcessCode;
      return Array.isArray(value) ? value.join(', ') : value || '';
    },
  },
];

const geographicColumns = [
  { accessorKey: 'tcProductCertifiedRawMaterialCode', header: 'Raw Material Code', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductCertifiedRawMaterialCodeDescription', header: 'Raw Material Description', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductCertifiedRawMaterialCountryOrArea', header: 'Country/Area', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductCertifiedRawMaterialCountryOrAreaDescription', header: 'Country/Area Description', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductCertifiedRawMaterialStateOrProvince', header: 'State/Province', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductCertifiedRawMaterialStateOrProvinceDescription', header: 'State/Province Description', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductCertifiedRawMaterialWeight', header: 'Weight', minSize: 60, maxSize: 150 },
];

const materialColumns = [
  { accessorKey: 'tcProductInfoFiberLength', header: 'Fiber Length (mm)', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductInfoYarnCount', header: 'Yarn Count', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductInfoFabricWeightAndConstruct', header: 'Fabric Weight and Construct (kg)', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductInfoFabricWeightOfFinishedProduct', header: 'Fabric Weight of Finished Product (kg)', minSize: 60, maxSize: 150 },
];

const standardColumns = [
  { accessorKey: 'tcProductClaimsCategory', header: 'Claims Category', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductRawMaterialCodeClaimsCategory', header: 'Raw Material', minSize: 60, maxSize: 150 },
  {
    accessorKey: 'tcProductCertifiedWeight',
    header: 'Certified Weight (kg)',
    minSize: 60,
    maxSize: 150,
    Cell: ({ row }: any) =>
      row.original.tcProductClaimsCategoryCertifiedWeight ??
      row.original.tcProductCertifiedWeight ??
      '',
  },
  { accessorKey: 'tcProductRawMaterialCodeClaimsCategoryDescription', header: 'Raw Material Description', minSize: 60, maxSize: 150 }
];

const rawMaterialColumns = [
  { accessorKey: 'tcProductRawMaterialCode', header: 'Raw Material Code', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductRawMaterialPercentage', header: 'Raw Material Percentage', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductRawMaterialCodeDescription', header: 'Raw Material Description', minSize: 60, maxSize: 150 },
];

const componentColumns = [
  {
    accessorKey: 'tcProductComponentNo',
    header: 'Component Number',
    minSize: 60,
    maxSize: 150,
    Cell: ({ row }: any) => row.original.tcProductComponentNo || row.original.tcComponentNumber || row.original.componentNo || '',
  },
  {
    accessorKey: 'tcProductComponentDetail',
    header: 'Component Detail',
    minSize: 60,
    maxSize: 150,
    Cell: ({ row }: any) => row.original.tcProductComponentDetail || row.original.tcComponentDetail || row.original.componentDetail || '',
  },
  {
    accessorKey: 'tcProductComponentNetShippingWeight',
    header: 'Component Net Shipping Weight (kg)',
    minSize: 60,
    maxSize: 180,
    Cell: ({ row }: any) => {
      const v = row.original.tcProductComponentNetShippingWeight ?? row.original.tcComponentNetShippingWeight ?? row.original.componentNetShippingWeight;
      return formatTcV4Decimal(v);
    },
  },
  {
    accessorKey: 'tcProductComponentCertifiedWeight',
    header: 'Component Certified Weight (kg)',
    minSize: 60,
    maxSize: 180,
    Cell: ({ row }: any) => {
      const component = row.original;
      const directCertified = component.componentCertifiedWeight ?? component.tcComponentCertifiedWeight ?? component.tcProductComponentCertifiedWeight;
      if (directCertified !== undefined && directCertified !== null && directCertified !== "") {
        return formatTcV4Decimal(directCertified);
      }
      const certified = (component.tcProductComponentCertifiedWeightInformation || component.tcComponentCertifiedWeightInformation || []).reduce(
        (s: number, w: any) =>
          s +
          (Number(
            w.tcProductComponentCertifiedWeight ??
              w.tcProductComponentClaimsCategoryCertifiedWeight ??
              w.tcComponentCertifiedWeight ??
              w.tcComponentClaimsCategoryCertifiedWeight
          ) || 0),
        0
      );
      return formatTcV4Decimal(certified);
    },
  },
  {
    accessorKey: 'tcProductComponentNonCertifiedWeight',
    header: 'Component Non-Certified Weight (kg)',
    minSize: 60,
    maxSize: 180,
    Cell: ({ row }: any) => {
      const component = row.original;
      const directNonCertified = component.componentNonCertifiedWeight ?? component.tcComponentNonCertifiedWeight ?? component.tcProductComponentNonCertifiedWeight;
      if (directNonCertified !== undefined && directNonCertified !== null && directNonCertified !== "") {
        return formatTcV4Decimal(directNonCertified);
      }
      const net = Number(component.tcProductComponentNetShippingWeight ?? component.tcComponentNetShippingWeight ?? component.componentNetShippingWeight) || 0;
      const certified = (component.tcProductComponentCertifiedWeightInformation || component.tcComponentCertifiedWeightInformation || []).reduce(
        (s: number, w: any) =>
          s +
          (Number(
            w.tcProductComponentCertifiedWeight ??
              w.tcProductComponentClaimsCategoryCertifiedWeight ??
              w.tcComponentCertifiedWeight ??
              w.tcComponentClaimsCategoryCertifiedWeight
          ) || 0),
        0
      );
      return formatTcV4Decimal(net - certified);
    },
  },
];


const isDataEmpty = (data: any) => {
  if (!data) return true;
  if (Array.isArray(data)) return data.length === 0;
  return Object.values(data).every(
    (val) => val === null || val === undefined || String(val).trim() === "" || String(val).trim().toLowerCase() === "na"
  );
};

const RenderDetailPanel = memo(({ row }: RenderDetailPanelProps) => {
  const rowData = row.original;
  const { TE_ID } = useParams();
  const { getCurrentFormValues } = useFormActions("Transaction-Details");
  const returnTcId = getCurrentFormValues()?.returnTcId;
  const isDraft = !!rowData?.isReturnAutofillDraft;
  const sourceTcId = isDraft ? returnTcId : TE_ID;

  const { loading, executeGet } = useApi();
  const [productData, setProductData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const productId = rowData?.productId;
  const shipmentId = rowData?.shipmentId;
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (isDraft) {
      const store = useTransactionCertificateStore.getState();
      const returnProducts = store.returnTcProducts || [];
      const match = returnProducts.find(
        (p: any) => p.productId === productId || p.tcProductArticleNo === rowData?.tcProductArticleNo
      );
      if (match) {
        setProductData([match]);
      } else {
        setProductData([]);
      }
      return;
    }

    if (!productId || !sourceTcId || !shipmentId || hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    fetchProductaddDetails(executeGet, sourceTcId, productId, shipmentId)
      .then((data) => {
        const tcProductInformation = data?.data?.tcProductInformation || [data?.data] || [data] || [];
        setProductData(tcProductInformation);
      })
      .catch((err) => {setError(err.message)});
  }, [productId, shipmentId, sourceTcId, executeGet, isDraft, rowData]);

  if (loading) {
    return <LoaderComponent />;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  if (!productData || productData.length === 0) {
    return <Typography>No product details available.</Typography>;
  }

  // Data Extraction: Since productData is an array
  const store = useTransactionCertificateStore.getState();
  const product = productData[0]; // Assuming one item per detail panel (adjust if multiple)

  const firstProcessorInfo = product.tcProductFirstProcessorInformation || [];
  const facilitiesInfo = normalizeProductFacilityInformation(
    product.tcProductFacilityInformation || product.tcProductFacilitiesInformation || []
  );
  const geographicInfo = flattenCertifiedRawMaterialGeographicInformation(
    product.tcProductCertifiedRawMaterialGeographicInformation || []
  ).map((info: any) => {
    const countryList = Array.isArray(store?.getCountryCodes) ? store.getCountryCodes : [];
    const countryDesc = countryList.find((option: any) => option.value === info.tcProductCertifiedRawMaterialCountryOrArea)?.label || info.tcProductCertifiedRawMaterialCountryOrAreaDescription || "";
    return {
      ...info,
      tcProductCertifiedRawMaterialCountryOrAreaDescription: countryDesc,
      tcProductCertifiedRawMaterialStateOrProvinceDescription: info.tcProductCertifiedRawMaterialStateOrProvinceDescription || "",
    };
  });

  const materialInfo = {
    tcProductInfoFiberLength: product.tcProductInfoFiberLength,
    tcProductInfoYarnCount: product.tcProductInfoYarnCount,
    tcProductInfoFabricWeightAndConstruct: product.tcProductInfoFabricWeightAndConstruct,
    tcProductInfoFabricWeightOfFinishedProduct: product.tcProductInfoFabricWeightOfFinishedProduct
  };

  const standardInfo = product.tcProductCertifiedWeightInformation || [];
  const rawMaterialInfo = product.tcProductRawMaterialInformation || [];
  const compList = (product.tcProductComponentInformation || product.components || []).filter(
    (c: any) =>
      c &&
      (c.tcProductComponentNo || c.tcComponentNumber || c.componentNo || c.tcProductComponentDetail || c.tcComponentDetail || c.componentDetail) &&
      String(c.tcProductComponentNo || c.tcComponentNumber || c.componentNo || "").trim().toLowerCase() !== "na"
  );



  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 4,
        gap: 4.5,
        width: '100%',
        backgroundColor: '#ffffff',
        borderRadius: '8px',

        border: '1.5px solid #ffb300',
        boxShadow: '0px 4px 20px #ffb30018',
        boxSizing: 'border-box',
        '& .MuiPaper-root': {
          boxShadow: 'none !important',
          border: '1px solid #e2e8f0',
          borderRadius: '6px',
          overflow: 'hidden',
          backgroundColor: '#ffffff !important',
        },

        '& .MuiTableCell-head': {
          backgroundColor: '#E1ECEB !important',
          color: '#2C4748 !important',
          height: 'auto !important',
          py: '8px !important',
          borderBottom: '1px solid #cbd5e1 !important',
        },
        '& .MuiTableCell-head *': {
          color: '#2C4748 !important',
          fontWeight: '600 !important',
          fontSize: '0.75rem !important',
          textTransform: 'uppercase !important',
          letterSpacing: '0.03em !important',
        },
        '& .MuiTableCell-body': {
          py: '8px !important',
          fontSize: '0.8rem !important',
          color: '#475569 !important',
          borderBottom: '1px solid #f1f5f9 !important',
        },
        '& .MuiTableRow-root:last-child .MuiTableCell-body': {
          borderBottom: 'none !important',
        },
        '& .MuiDivider-root': {
          width: '100%',
          borderColor: '#e2e8f0',
          my: 1,
        }
      }}
    >
      {/* Product General Information */}
      <Box sx={{ width: '100%', textAlign: 'left', mb: 3 }} display="grid">
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            fontSize: '0.85rem',
            color: '#2C4748',
            mb: 1.5,
            display: 'flex',
            alignItems: 'center',
            '&::before': {
              content: '""',
              display: 'inline-block',
              width: '4px',
              height: '14px',
              backgroundColor: '#2C4748',
              borderRadius: '2px',
              mr: 1,
            }
          }}
        >
          Product General Information
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 2, pl: 1.5 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>PRODUCT ARTICLE NUMBER</Typography>
            <Typography variant="body2">{product.tcProductArticleNo || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>PRODUCT CATEGORY CODE (PC)</Typography>
            <Typography variant="body2">{product.tcProductCategoryCodeDescription || product.tcProductCategoryCode || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>PRODUCT DETAIL CODE (PD)</Typography>
            <Typography variant="body2">{product.tcProductDetailCodeDescription || product.tcProductDetailCode || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>PRODUCTION DATE</Typography>
            <Typography variant="body2">{product.tcProductProductionDate || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>NET SHIPPING WEIGHT (KG)</Typography>
            <Typography variant="body2">{product.tcProductNetShippingWeight ?? '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>SUPPLEMENTARY WEIGHT (KG)</Typography>
            <Typography variant="body2">{product.tcProductSupplementaryWeight ?? '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>ADDITIVES WEIGHT (KG)</Typography>
            <Typography variant="body2">{product.tcProductAdditivesWeight ?? '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>ADDITIVES</Typography>
            <Typography variant="body2">{product.tcProductAdditives || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>GTIN</Typography>
            <Typography variant="body2">{product.tcProductGtin || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>NUMBER OF UNITS / METRIC</Typography>
            <Typography variant="body2">
              {product.tcProductNumberOfUnits ? `${product.tcProductNumberOfUnits} ${product.tcProductUnitsMetric || ''}` : '-'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>ORDER NUMBERS</Typography>
            <Typography variant="body2">
              {Array.isArray(product.tcProductOrderNo) ? product.tcProductOrderNo.join(', ') : product.tcProductOrderNo || '-'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>SHIPMENT DOCUMENT NUMBERS</Typography>
            <Typography variant="body2">
              {Array.isArray(product.tcProductShipmentDocNo) ? product.tcProductShipmentDocNo.join(', ') : product.tcProductShipmentDocNo || '-'}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Divider />

      {/* First Processor Info */}
      <Box sx={{ width: '100%', textAlign: 'left', mb: 3 }} display="grid">
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            fontSize: '0.85rem',
            color: '#2C4748',
            mb: 1.5,
            display: 'flex',
            alignItems: 'center',
            '&::before': {
              content: '""',
              display: 'inline-block',
              width: '4px',
              height: '14px',
              backgroundColor: '#2C4748',
              borderRadius: '2px',
              mr: 1,
            }
          }}
        >
          First Processor Information
          {isDraft && (
            <Chip 
              label="Review needed" 
              size="small" 
              sx={{ 
                ml: 2, 
                height: '20px', 
                fontSize: '0.7rem', 
                fontWeight: 600,
                backgroundColor: '#fee2e2',
                color: '#ef4444',
                border: '1px solid #fca5a5'
              }} 
            />
          )}
        </Typography>
        {!isDataEmpty(firstProcessorInfo) ? (
          <CustomTable
            columns={firstProcessorColumns}
            data={firstProcessorInfo}
            enableTopToolbar={false}
            enableRowActions={false}
            enableBottomToolbar={false}
            enableSorting={false}
            enableColumnFilters={false}
          />
        ) : (
          <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic', pl: 1.5, fontSize: '0.8rem' }}>
            No records to display
          </Typography>
        )}
      </Box>
      <Divider />

      {/* Fabric & Material Details */}
      <Box sx={{ width: '100%', textAlign: 'left', mb: 3 }} display="grid">
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            fontSize: '0.85rem',
            color: '#2C4748',
            mb: 1.5,
            display: 'flex',
            alignItems: 'center',
            '&::before': {
              content: '""',
              display: 'inline-block',
              width: '4px',
              height: '14px',
              backgroundColor: '#2C4748',
              borderRadius: '2px',
              mr: 1,
            }
          }}
        >
          Fabric and Material Details
          {isDraft && (
            <Chip 
              label="Review needed" 
              size="small" 
              sx={{ 
                ml: 2, 
                height: '20px', 
                fontSize: '0.7rem', 
                fontWeight: 600,
                backgroundColor: '#fee2e2',
                color: '#ef4444',
                border: '1px solid #fca5a5'
              }} 
            />
          )}
        </Typography>
        {!isDataEmpty(materialInfo) ? (
          <CustomTable
            columns={materialColumns}
            data={[materialInfo]}
            enableTopToolbar={false}
            enableRowActions={false}
            enableBottomToolbar={false}
            enableSorting={false}
            enableColumnFilters={false}
          />
        ) : (
          <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic', pl: 1.5, fontSize: '0.8rem' }}>
            No records to display
          </Typography>
        )}
      </Box>
      <Divider />

      {/* Standard Details */}
      <Box sx={{ width: '100%', textAlign: 'left', mb: 3 }} display="grid">
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            fontSize: '0.85rem',
            color: '#2C4748',
            mb: 1.5,
            display: 'flex',
            alignItems: 'center',
            '&::before': {
              content: '""',
              display: 'inline-block',
              width: '4px',
              height: '14px',
              backgroundColor: '#2C4748',
              borderRadius: '2px',
              mr: 1,
            }
          }}
        >
          Standard Details
          {isDraft && (
            <Chip 
              label="Review needed" 
              size="small" 
              sx={{ 
                ml: 2, 
                height: '20px', 
                fontSize: '0.7rem', 
                fontWeight: 600,
                backgroundColor: '#fee2e2',
                color: '#ef4444',
                border: '1px solid #fca5a5'
              }} 
            />
          )}
        </Typography>
        {!isDataEmpty(standardInfo) ? (
          <CustomTable
            columns={standardColumns}
            data={standardInfo}
            enableTopToolbar={false}
            enableRowActions={false}
            enableBottomToolbar={false}
            enableSorting={false}
            enableColumnFilters={false}
          />
        ) : (
          <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic', pl: 1.5, fontSize: '0.8rem' }}>
            No records to display
          </Typography>
        )}
      </Box>
      <Divider />

      {/* Raw Material Composition */}
      <Box sx={{ width: '100%', textAlign: 'left', mb: 3 }} display="grid">
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            fontSize: '0.85rem',
            color: '#2C4748',
            mb: 1.5,
            display: 'flex',
            alignItems: 'center',
            '&::before': {
              content: '""',
              display: 'inline-block',
              width: '4px',
              height: '14px',
              backgroundColor: '#2C4748',
              borderRadius: '2px',
              mr: 1,
            }
          }}
        >
          Raw Material Composition
        </Typography>
        {!isDataEmpty(rawMaterialInfo) ? (
          <CustomTable
            columns={rawMaterialColumns}
            data={rawMaterialInfo}
            enableTopToolbar={false}
            enableRowActions={false}
            enableBottomToolbar={false}
            enableSorting={false}
            enableColumnFilters={false}
          />
        ) : (
          <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic', pl: 1.5, fontSize: '0.8rem' }}>
            No records to display
          </Typography>
        )}
      </Box>
      <Divider />

      {/* Facilities Information */}
      <Box sx={{ width: '100%', textAlign: 'left', mb: 3 }} display="grid">
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            fontSize: '0.85rem',
            color: '#2C4748',
            mb: 1.5,
            display: 'flex',
            alignItems: 'center',
            '&::before': {
              content: '""',
              display: 'inline-block',
              width: '4px',
              height: '14px',
              backgroundColor: '#2C4748',
              borderRadius: '2px',
              mr: 1,
            }
          }}
        >
          Facilities Information
          {isDraft && (
            <Chip 
              label="Review needed" 
              size="small" 
              sx={{ 
                ml: 2, 
                height: '20px', 
                fontSize: '0.7rem', 
                fontWeight: 600,
                backgroundColor: '#fee2e2',
                color: '#ef4444',
                border: '1px solid #fca5a5'
              }} 
            />
          )}
        </Typography>
        {!isDataEmpty(facilitiesInfo) ? (
          <CustomTable
            columns={facilitiesColumns}
            data={facilitiesInfo}
            enableTopToolbar={false}
            enableRowActions={false}
            enableBottomToolbar={false}
            enableSorting={false}
            enableColumnFilters={false}
          />
        ) : (
          <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic', pl: 1.5, fontSize: '0.8rem' }}>
            No records to display
          </Typography>
        )}
      </Box>
      <Divider />

      {/* Certified Raw Material Geographic Information */}
      <Box sx={{ width: '100%', textAlign: 'left', mb: 3 }} display="grid">
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            fontSize: '0.85rem',
            color: '#2C4748',
            mb: 1.5,
            display: 'flex',
            alignItems: 'center',
            '&::before': {
              content: '""',
              display: 'inline-block',
              width: '4px',
              height: '14px',
              backgroundColor: '#2C4748',
              borderRadius: '2px',
              mr: 1,
            }
          }}
        >
          Certified Raw Material Geographic Information
          {isDraft && (
            <Chip 
              label="Review needed" 
              size="small" 
              sx={{ 
                ml: 2, 
                height: '20px', 
                fontSize: '0.7rem', 
                fontWeight: 600,
                backgroundColor: '#fee2e2',
                color: '#ef4444',
                border: '1px solid #fca5a5'
              }} 
            />
          )}
        </Typography>
        {!isDataEmpty(geographicInfo) ? (
          <CustomTable
            columns={geographicColumns}
            data={geographicInfo}
            enableTopToolbar={false}
            enableRowActions={false}
            enableBottomToolbar={false}
            enableSorting={false}
            enableColumnFilters={false}
          />
        ) : (
          <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic', pl: 1.5, fontSize: '0.8rem' }}>
            No records to display
          </Typography>
        )}
      </Box>
      <Divider />

      {/* Component Information (product-without-component → NA) */}
      <Box sx={{ width: '100%', textAlign: 'left' }} display="grid">
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            fontSize: '0.85rem',
            color: '#2C4748',
            mb: 1.5,
            display: 'flex',
            alignItems: 'center',
            '&::before': {
              content: '""',
              display: 'inline-block',
              width: '4px',
              height: '14px',
              backgroundColor: '#2C4748',
              borderRadius: '2px',
              mr: 1,
            }
          }}
        >
          Component Information
        </Typography>
        {compList.length > 0 ? (
          <CustomTable
            columns={componentColumns}
            data={compList}
            enableTopToolbar={false}
            enableRowActions={false}
            enableBottomToolbar={false}
            enableSorting={false}
            enableColumnFilters={false}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            Component Number: NA
          </Typography>
        )}
      </Box>
    </Box>
  );
});

export default RenderDetailPanel;
